import { useNode, useEditor } from "@craftjs/core";
import { ROOT_NODE } from "@craftjs/utils";
import React, { useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import styled from "@emotion/styled";
import { ArrowUp, Trash, ArrowsMove } from "@modir/icons";

const IndicatorDiv = styled.div`
  position: sticky;
  height: 29px;
  margin-top: -34px;
  font-size: 12px;
  line-height: 12px;
  display: flex;
  background-color: #3e8ce4;
  color: #fff;
  position: fixed;
  align-items: center;
  padding-left: 10px;
  border-radius: 3px;
  z-index: 5;
  margin-left: 10px;
  font-size: 13px;
  flex-shrink: 0;

  svg {
    fill: #fff;
    width: 12px;
    height: 12px;
  }
`;

const IndicatorHeader = styled.div`
  flex-grow: 1;
  text-transform: capitalize;
  cursor: move;
  padding-left: 0;
  padding-right: 15px;

  svg {
    margin-right: 5px;
  }
`;

const Btn = styled.a`
  padding: 0 7px;
  border-left: 1px solid rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  height: 100%;
  cursor: pointer;

  :hover {
    background-color: #387ecd;
  }

  :last-of-type {
    border-radius: 0 3px 3px 0;
  }
`;

export const RenderNode = ({ render }: any) => {
  const { id } = useNode();
  const { actions, query, isActive } = useEditor((_, query) => ({
    isActive: query.getEvent("selected").contains(id),
  }));

  const {
    isHover,
    dom,
    name,
    moveable,
    deletable,
    connectors: { drag },
    parent,
  } = useNode((node) => ({
    isHover: node.events.hovered,
    dom: node.dom,
    name: node.data.custom.displayName || node.data.displayName,
    moveable: query.node(node.id).isDraggable(),
    deletable: query.node(node.id).isDeletable(),
    parent: node.data.parent,
    props: node.data.props,
  }));

  const currentRef = useRef<HTMLDivElement | any>();

  useEffect(() => {
    if (dom) {
      if (isActive || isHover) dom.classList.add("component-selected");
      else dom.classList.remove("component-selected");
    }
  }, [dom, isActive, isHover]);

  const getPos = useCallback((dom: HTMLElement) => {
    const { top, left, bottom } = dom
      ? dom.getBoundingClientRect()
      : { top: 0, left: 0, bottom: 0 };
    return {
      top: `${top > 0 ? top : bottom}px`,
      left: `${left}px`,
    };
  }, []);

  const scroll = useCallback(() => {
    const { current: currentDOM } = currentRef;

    if (!currentDOM) return;
    if (!dom) return;
    const { top, left } = getPos(dom);
    currentDOM.style.top = top;
    currentDOM.style.left = left;
  }, [dom, getPos]);

  useEffect(() => {
    document?.querySelector(".main-canvas")?.addEventListener("scroll", scroll);

    return () => {
      document
        ?.querySelector(".main-canvas")
        ?.removeEventListener("scroll", scroll);
    };
  }, [scroll]);

  return (
    <>
      {isHover || isActive
        ? ReactDOM.createPortal(
            <IndicatorDiv
              ref={currentRef}
              style={{
                left: dom ? getPos(dom).left : 0,
                top: dom ? getPos(dom).top : 0,
                zIndex: 9999,
                position: "fixed",
              }}
            >
              <IndicatorHeader ref={drag as any}>
                {moveable ? <ArrowsMove /> : null}
                {name}
              </IndicatorHeader>

              {id !== ROOT_NODE && (
                <Btn
                  onClick={() => {
                    actions.selectNode(parent as any);
                  }}
                >
                  <ArrowUp />
                </Btn>
              )}
              {deletable ? (
                <Btn
                  onMouseDown={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    actions.delete(id);
                  }}
                >
                  <Trash />
                </Btn>
              ) : null}
            </IndicatorDiv>,
            document.querySelector(".main-canvas") as any
          )
        : null}
      {render}
    </>
  );
};
